import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  FileDown,
  RefreshCcw,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader,
  XCircle,
  ThumbsDown,
  Truck,
  ShoppingBag,
  HelpCircle,
  FileText,
  Eye,
  Phone,
  MapPin,
  User,
  Hash,
  CalendarDays,
  Route,
  Package,
  ShieldAlert,
  Search,
  ClipboardList,
  BadgeCheck,
  MapPinned,
  Store,
  Bike,
  Activity,
  CalendarClock,
  ChevronDown,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Toaster } from "sonner";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import LoadingScreen from "../Utils/LoadingScreen";
import TablePagination from "../Utils/TablePagination";
import useTasks from "../../Sevices/useTasks";
import AssignModal from "../MainDashboard/AssignModal";
import TaskDetailsModal from "../MainDashboard/MapDashBoard/TaskDetailsModal";
import useDriver from "../../Sevices/Driver Api/useDriver";
import useDriverTeams from "../../Sevices/Driver Api/useDriverTeams";

export default function TasksPage() {
  const {
    fetchTasks,
    changeTaskStatus,
    deleteTask,
    fetchTaskLogs,
    taskGlobalSearch,
    fetchTaskDetails,
    loading,
    globalSearchLoading,
  } = useTasks();
  const { fetchDrivers, AssignTaskToDriver } = useDriver();
  const { teams, fetchDriverTeams } = useDriverTeams();

  const [tasks, setTasks] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("tasks");

  const [agents, setAgents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTask, setSelectedTask] = useState(null);
  const [assignmentLogs, setAssignmentLogs] = useState([]);
  const [statusChangeLogs, setStatusChangeLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  const [taskSearchWord, setTaskSearchWord] = useState("");
  const [taskSearchResults, setTaskSearchResults] = useState([]);
  const [hasTaskSearched, setHasTaskSearched] = useState(false);
  const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);

  const [searchShowLogs, setSearchShowLogs] = useState(false);
  const [searchAssignmentLogs, setSearchAssignmentLogs] = useState([]);
  const [searchStatusChangeLogs, setSearchStatusChangeLogs] = useState([]);
  const [searchLogsLoading, setSearchLogsLoading] = useState(false);
  const [selectedSearchStatus, setSelectedSearchStatus] = useState("");
  const [searchStatusUpdating, setSearchStatusUpdating] = useState(false);

  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [taskToAssign, setTaskToAssign] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [assignTab, setAssignTab] = useState("team");
  const [selectedAssignTeam, setSelectedAssignTeam] = useState("");
  const [pageInfo, setPageInfo] = useState({
    nextToken: "",
    previousToken: "",
    pageSize: 50,
    totalCount: 0,
    pageNumber: 1,
  });

  const handleAssignContext = (task) => {
    setTaskToAssign(task);
    setSelectedAgentId("");

    const taskShopCode =
      task?.shopcode || task?.shop_code || task?.restaurant_code;

    const matchedTeam = teams?.find((t) =>
      t.shopList?.some(
        (s) => s.shop_code?.toString() === taskShopCode?.toString(),
      ),
    );

    if (matchedTeam) {
      setSelectedAssignTeam(matchedTeam.team_name);
      setAssignTab("team");
    } else {
      setAssignTab("all");
    }

    setShowAssignModal(true);
  };

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(showLogs ? contentRef.current.scrollHeight : 0);
    }
  }, [showLogs, statusChangeLogs, assignmentLogs]);

  const perPage = 50;
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  });

  const formatDate = (date) => date.toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(formatDate(today));
  const [toDate, setToDate] = useState(formatDate(today));
  const [statusFilter, setStatusFilter] = useState("");

  const teamDrivers = useMemo(() => {
    if (!selectedAssignTeam) return [];

    const teamData = teams.find((t) => t.team_name === selectedAssignTeam);

    const ids = teamData?.driverlist?.map((d) => d.id?.toString()) || [];

    return (agents || []).filter(
      (a) =>
        ids.includes(a?.id?.toString()) ||
        ids.includes(a?.driverId?.toString()),
    );
  }, [selectedAssignTeam, teams, agents]);

  const allTeamDrivers = useMemo(() => {
    if (!selectedAssignTeam) return [];

    const teamData = teams.find((t) => t.team_name === selectedAssignTeam);

    const ids = teamData?.driverlist?.map((d) => d.id?.toString()) || [];

    return (agents || []).filter(
      (a) =>
        ids.includes(a?.id?.toString()) ||
        ids.includes(a?.driverId?.toString()),
    );
  }, [selectedAssignTeam, teams, agents]);

  const statusOptions = [
    "unassigned",
    "assigned",
    "started",
    "inprogress",
    "successful",
    "food is ready",
    "acknowledged",
    "failed",
    "declined",
    "cancelled",
    "picked up",
    "arrived",
  ];

  const handleTaskDetailsSearch = async () => {
    const taskId = taskSearchWord.trim();

    if (!taskId) {
      setHasTaskSearched(false);
      setTaskSearchResults([]);
      setSelectedSearchStatus("");
      setSearchShowLogs(true);
      setSearchAssignmentLogs([]);
      setSearchStatusChangeLogs([]);
      return;
    }

    setHasTaskSearched(true);
    setTaskDetailsLoading(true);

    setSearchShowLogs(true);
    setSearchAssignmentLogs([]);
    setSearchStatusChangeLogs([]);

    try {
      const data = await fetchTaskDetails(taskId);

      const safeData = Array.isArray(data) ? data : data ? [data] : [];

      setTaskSearchResults(safeData);

      if (safeData?.[0]?.status) {
        setSelectedSearchStatus(safeData[0].status.toLowerCase());
      } else {
        setSelectedSearchStatus("");
      }

      if (safeData?.[0]) {
        await fetchSearchLogsDirect(safeData[0]);
      }
    } catch (error) {
      console.error("Task details search error:", error);
      setTaskSearchResults([]);
      setSelectedSearchStatus("");
      setSearchAssignmentLogs([]);
      setSearchStatusChangeLogs([]);
    } finally {
      setTaskDetailsLoading(false);
    }
  };

  const handleSearchStatusChange = async (taskItem) => {
    const taskId =
      taskItem?.pk_task_id ||
      taskItem?.taskId ||
      taskItem?.task_id ||
      taskItem?.id;

    if (!taskId || !selectedSearchStatus) return;

    const currentStatus = taskItem?.status?.toLowerCase();

    if (
      selectedSearchStatus === "unassigned" &&
      currentStatus !== "unassigned"
    ) {
      setTaskToAssign(taskItem);
      setSelectedAgentId("");

      setAssignTab("all");
      handleAssignContext(taskItem);

      return;
    }

    setSearchStatusUpdating(true);

    try {
      await changeTaskStatus(taskItem, selectedSearchStatus, async () => {
        const updatedTask = await fetchTaskDetails(taskId);

        const safeData = Array.isArray(updatedTask)
          ? updatedTask
          : updatedTask
            ? [updatedTask]
            : [];

        setTaskSearchResults(safeData);

        if (safeData?.[0]?.status) {
          setSelectedSearchStatus(safeData[0].status.toLowerCase());
        } else {
          setSelectedSearchStatus("");
        }

        if (safeData?.[0]) {
          await handleToggleSearchLogs(safeData[0]);
        } else {
          setSearchShowLogs(true);
          setSearchAssignmentLogs([]);
          setSearchStatusChangeLogs([]);
        }
      });
    } catch (error) {
      console.error("Search status update error:", error);
    } finally {
      setSearchStatusUpdating(false);
    }
  };

  const handleTaskTabGlobalSearch = async () => {
    const keyword = search.trim();

    if (!keyword) {
      fetchTasks(fromDate, toDate, setTasks, {
        pageSize: pageInfo.pageSize,
        nextToken: "",
        previousToken: "",
        setPageInfo,
      });
      return;
    }

    const data = await taskGlobalSearch({
      word: keyword,
      fromDate,
      toDate,
    });

    const safeData = Array.isArray(data) ? data : data ? [data] : [];

    setTasks(safeData);
    setCurrentPage(1);
  };

  const filtered = tasks.filter(
    (task) => !statusFilter || task.status === statusFilter,
  );

  const getOnlyDate = (value) => {
    if (!value) return formatDate(new Date());

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return value?.toString()?.split("T")?.[0] || formatDate(new Date());
    }

    return date.toISOString().split("T")[0];
  };

  const fetchSearchLogsDirect = async (taskItem) => {
    const id =
      taskItem?.pk_task_id ||
      taskItem?.taskId ||
      taskItem?.task_id ||
      taskItem?.id;

    if (!id) return;

    setSearchShowLogs(true);
    setSearchLogsLoading(true);

    try {
      const logDate = getOnlyDate(
        taskItem?.date_created ||
          taskItem?.delivery_date ||
          taskItem?.date_modified,
      );

      const data = await fetchTaskLogs(id, logDate, logDate);

      console.log("Search Logs Response:", data);

      setSearchAssignmentLogs(data?.assignmentLogs || []);
      setSearchStatusChangeLogs(data?.statusChangeLogs || []);
    } catch (error) {
      console.error("Search task logs error:", error);
      setSearchAssignmentLogs([]);
      setSearchStatusChangeLogs([]);
    } finally {
      setSearchLogsLoading(false);
    }
  };

  const handleToggleSearchLogs = async (taskItem) => {
    const id =
      taskItem?.pk_task_id ||
      taskItem?.taskId ||
      taskItem?.task_id ||
      taskItem?.id;

    if (!id) return;

    setSearchShowLogs(true);
    setSearchLogsLoading(true);

    try {
      const logDate = getOnlyDate(
        taskItem?.date_created ||
          taskItem?.delivery_date ||
          taskItem?.date_modified,
      );

      const data = await fetchTaskLogs(id, logDate, logDate);

      console.log("Search Logs Response:", data);

      setSearchAssignmentLogs(data?.assignmentLogs || []);
      setSearchStatusChangeLogs(data?.statusChangeLogs || []);
    } catch (error) {
      console.error("Search task logs error:", error);
      setSearchAssignmentLogs([]);
      setSearchStatusChangeLogs([]);
    } finally {
      setSearchLogsLoading(false);
    }
  };

  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  // const current = filtered.slice(indexOfFirst, indexOfLast);
  const current = filtered;
  const totalPages = Math.ceil(filtered.length / perPage);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchTasks(fromDate, toDate, setTasks, {
      pageSize: pageInfo.pageSize,
      nextToken: "",
      previousToken: "",
      setPageInfo,
    });
  };

  const renderStatusBadge = (status) => {
    const statusMap = {
      unassigned: {
        color: "bg-blue-100 text-blue-700",
        icon: <HelpCircle className="w-4 h-4 mr-1" />,
        label: "Unassigned",
      },
      assigned: {
        color: "bg-yellow-100 text-yellow-800",
        icon: <Clock className="w-4 h-4 mr-1" />,
        label: "Assigned",
      },
      started: {
        color: "bg-amber-100 text-amber-800",
        icon: <Loader className="w-4 h-4 mr-1 animate-spin" />,
        label: "Started",
      },
      inprogress: {
        color: "bg-indigo-100 text-indigo-700",
        icon: <Loader className="w-4 h-4 mr-1 animate-spin" />,
        label: "In Progress",
      },
      "food is ready": {
        color: "bg-purple-100 text-purple-700",
        icon: <Truck className="w-4 h-4 mr-1" />,
        label: "Food Ready",
      },
      "picked up": {
        color: "bg-orange-100 text-orange-700",
        icon: <ShoppingBag className="w-4 h-4 mr-1" />,
        label: "Picked Up",
      },
      acknowledged: {
        color: "bg-teal-100 text-teal-700",
        icon: <CheckCircle className="w-4 h-4 mr-1" />,
        label: "Acknowledged",
      },
      successful: {
        color: "bg-green-100 text-green-700",
        icon: <CheckCircle className="w-4 h-4 mr-1" />,
        label: "Successful",
      },
      failed: {
        color: "bg-red-100 text-red-700",
        icon: <XCircle className="w-4 h-4 mr-1" />,
        label: "Failed",
      },
      declined: {
        color: "bg-pink-100 text-pink-700",
        icon: <ThumbsDown className="w-4 h-4 mr-1" />,
        label: "Declined",
      },
      cancelled: {
        color: "bg-gray-200 text-gray-700",
        icon: <AlertTriangle className="w-4 h-4 mr-1" />,
        label: "Cancelled",
      },
    };

    const { color, icon, label } = statusMap[status] || {
      color: "bg-slate-100 text-slate-700",
      icon: <HelpCircle className="w-4 h-4 mr-1" />,
      label: status,
    };

    return (
      <span
        className={`inline-flex items-center justify-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}
        style={{ minWidth: "130px" }}
      >
        {icon}
        <span className="truncate">{label}</span>
      </span>
    );
  };
  const formatDateTime = (value) => {
    if (!value) return "N/A";
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  const InfoBadge = ({ children, color = "gray" }) => {
    const styles = {
      gray: "bg-gray-100 text-gray-700",
      blue: "bg-blue-100 text-blue-700",
      green: "bg-green-100 text-green-700",
      orange: "bg-orange-100 text-orange-700",
      red: "bg-red-100 text-red-700",
      purple: "bg-purple-100 text-purple-700",
      yellow: "bg-yellow-100 text-yellow-700",
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[color]}`}
      >
        {children}
      </span>
    );
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (typeof value === "object") return JSON.stringify(value);
    return value.toString();
  };

  const formatDisplayDateTime = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);
    if (isNaN(date.getTime())) return value;

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const MiniInfo = ({ icon: Icon, label, value }) => (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-1.5">
      <div className=" flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        {Icon && <Icon size={11} />}
        {label}
      </div>

      <p className="break-words text-xs font-semibold leading-4 text-gray-900">
        {formatValue(value)}
      </p>
    </div>
  );

  const StatusPill = ({ status }) => {
    const value = status?.toLowerCase();

    const colorMap = {
      unassigned: "bg-gray-100 text-gray-700 border-gray-200",
      assigned: "bg-yellow-50 text-yellow-700 border-yellow-200",
      acknowledged: "bg-blue-50 text-blue-700 border-blue-200",
      arrived: "bg-emerald-50 text-emerald-700 border-emerald-200",
      "food is ready": "bg-purple-50 text-purple-700 border-purple-200",
      "picked up": "bg-orange-50 text-orange-700 border-orange-200",
      successful: "bg-green-50 text-green-700 border-green-200",
      completed: "bg-green-50 text-green-700 border-green-200",
      cancelled: "bg-red-50 text-red-700 border-red-200",
      failed: "bg-red-50 text-red-700 border-red-200",
    };

    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
          colorMap[value] || "bg-gray-100 text-gray-700 border-gray-200"
        }`}
      >
        <BadgeCheck size={13} />
        {status || "N/A"}
      </span>
    );
  };

  const getShortAddress = (address, length = 52) => {
    if (!address) return "N/A";
    if (address.length <= length) return address;
    return `${address.slice(0, length)}...`;
  };

  useEffect(() => {
    fetchTasks(fromDate, toDate, setTasks, {
      pageSize: pageInfo.pageSize,
      nextToken: "",
      previousToken: "",
      setPageInfo,
    });
  }, []);

  useEffect(() => {
    const total = Math.max(1, Math.ceil(filtered.length / perPage));
    if (currentPage > total) {
      setCurrentPage(total);
    }
  }, [filtered.length, perPage, currentPage]);

  useEffect(() => {
    fetchDriverTeams();
    fetchDrivers(true).then(setAgents);
  }, []);

  const handleNextPage = () => {
    if (!pageInfo.nextToken) return;

    fetchTasks(fromDate, toDate, setTasks, {
      pageSize: pageInfo.pageSize,
      nextToken: pageInfo.nextToken,
      previousToken: "",
      setPageInfo,
    });
  };

  const handlePreviousPage = () => {
    if (!pageInfo.previousToken) return;

    fetchTasks(fromDate, toDate, setTasks, {
      pageSize: pageInfo.pageSize,
      nextToken: "",
      previousToken: pageInfo.previousToken,
      setPageInfo,
    });
  };

  return (
    <div className="p-6">
      <Toaster position="top-right" />

      <div className="mb-5 border-b border-gray-200">
        <div className="flex items-center gap-8">
          <button
            type="button"
            onClick={() => setActiveTab("tasks")}
            className={`relative pb-3 text-sm font-medium transition ${
              activeTab === "tasks"
                ? "text-orange-600"
                : "text-gray-500 hover:text-orange-600"
            }`}
          >
            Tasks
            {activeTab === "tasks" && (
              <span className="absolute bottom-0 left-0 h-[2px] w-full bg-orange-500" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("search")}
            className={`relative pb-3 text-sm font-medium transition ${
              activeTab === "search"
                ? "text-orange-600"
                : "text-gray-500 hover:text-orange-600"
            }`}
          >
            Search By Id
            {activeTab === "search" && (
              <span className="absolute bottom-0 left-0 h-[2px] w-full bg-orange-500" />
            )}
          </button>
        </div>
      </div>

      {activeTab === "tasks" && (
        <>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label className="text-xs font-medium block mb-1">From</label>
              <input
                type="date"
                className="border rounded px-2 py-1 text-xs w-[120px]"
                value={fromDate}
                max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div>
              <label className="text-xs font-medium block mb-1">To</label>
              <input
                type="date"
                className="border rounded px-2 py-1 text-xs w-[120px]"
                value={toDate}
                min={fromDate}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <button
              onClick={handleApplyFilters}
              className="flex items-center justify-center space-x-1 bg-green-600 min-w-[75px] h-7 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs shadow"
            >
              Apply
            </button>

            <div>
              <label className="text-xs font-medium block mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2 py-1 text-xs border rounded w-[150px]"
              >
                <option value="">All</option>
                <option value="unassigned">Unassigned</option>
                <option value="picked up">Picked Up</option>
                <option value="started">Started</option>
                <option value="inprogress">In Progress</option>
                <option value="food is ready">Food Ready</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="successful">Successful</option>
                <option value="failed">Failed</option>
                <option value="declined">Declined</option>
                <option value="cancelled">Cancelled</option>
                <option value="arrived">Arrived</option>
                <option value="assigned">Assigned</option>
              </select>
            </div>

            <div className="flex-1">
              <label className="text-xs font-medium block mb-1">Search</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearch(value);

                    if (!value.trim()) {
                      fetchTasks(fromDate, toDate, setTasks, {
                        pageSize: pageInfo.pageSize,
                        nextToken: "",
                        previousToken: "",
                        setPageInfo,
                      });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleTaskTabGlobalSearch();
                    }
                  }}
                  placeholder="Search task, order, customer, driver..."
                  className="min-w-[300px] px-2 py-1 border rounded text-xs"
                />

                <button
                  type="button"
                  onClick={handleTaskTabGlobalSearch}
                  disabled={globalSearchLoading}
                  className="flex items-center space-x-1 bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded text-xs shadow disabled:opacity-60"
                >
                  {globalSearchLoading ? (
                    <>
                      <Loader size={14} className="animate-spin" />
                      <span>Searching...</span>
                    </>
                  ) : (
                    <>
                      <Search size={14} />
                      <span>Search</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                setSearch("");
                fetchTasks(fromDate, toDate, setTasks, {
                  pageSize: pageInfo.pageSize,
                  nextToken: "",
                  previousToken: "",
                  setPageInfo,
                });
              }}
              className="flex items-center space-x-1 bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs shadow"
            >
              <RefreshCcw size={14} /> <span>Refresh</span>
            </button>

            <button
              className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs shadow"
              onClick={() =>
                exportAnyJsonToExcel({
                  data: tasks,
                  filePrefix: "TaskExport",
                  sheetName: "TasksList",
                })
              }
            >
              <FileDown size={14} /> <span>Export</span>
            </button>
          </div>

          {loading || globalSearchLoading ? (
            <LoadingScreen />
          ) : (
            <>
              <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  {current.length > 0 ? (
                    <table className="min-w-full">
                      <thead className="bg-orange-400 text-white border-b border-gray-200">
                        <tr className="text-left">
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-center">
                            T.I.D
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                            Task / Order
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                            Customer / Contact
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                            Route / Schedule
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                            Driver / Delivery
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                            Status
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-center">
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-100 bg-white">
                        {current.map((task) => {
                          const itemCount =
                            task?.delivery_details?.items?.length || 0;

                          return (
                            <tr
                              key={task.pk_task_id}
                              className="align-top transition-colors duration-200 hover:bg-orange-50/40"
                            >
                              <td className="px-4 py-4 text-center">
                                <div className="mx-auto flex h-8 w-8 items-center justify-center text-xs font-bold text-orange-700">
                                  {task.pk_task_id}
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                <div className="min-w-[220px] space-y-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-semibold text-gray-900 leading-none">
                                      {task.task_description || "N/A"}
                                    </p>

                                    {task.critical === 1 && (
                                      <InfoBadge color="red">
                                        <span className="inline-flex items-center gap-1">
                                          <ShieldAlert size={12} />
                                          Critical
                                        </span>
                                      </InfoBadge>
                                    )}
                                  </div>

                                  <div className="flex flex-wrap gap-1.5">
                                    <InfoBadge color="blue">
                                      <span className="inline-flex items-center gap-1">
                                        <Hash size={11} />
                                        Task #{task.pk_task_id || "N/A"}
                                      </span>
                                    </InfoBadge>

                                    <InfoBadge color="green">
                                      Order #{task.order_id || "N/A"}
                                    </InfoBadge>

                                    <InfoBadge color="purple">
                                      Daily #{task.merchant_order_id || "N/A"}
                                    </InfoBadge>
                                  </div>

                                  <div className="text-xs text-gray-500">
                                    <span className="font-medium text-gray-600">
                                      Type:
                                    </span>{" "}
                                    {task.trans_type || "N/A"}
                                    {task.shop_code
                                      ? ` • Shop: ${task.shop_code}`
                                      : ""}
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                <div className="min-w-[220px] space-y-2 text-xs text-gray-700">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <User
                                      size={13}
                                      className="text-gray-400 shrink-0"
                                    />
                                    <span className="font-medium text-gray-900 truncate">
                                      {task.customer_name || "N/A"}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2 min-w-0">
                                    <Phone
                                      size={13}
                                      className="text-gray-400 shrink-0"
                                    />
                                    <span className="truncate">
                                      {task.contact_number || "N/A"}
                                    </span>
                                  </div>

                                  <div className="flex items-start gap-2 min-w-0">
                                    <MapPin
                                      size={13}
                                      className="text-gray-400 shrink-0 mt-0.5"
                                    />
                                    <span className="leading-5 text-gray-600">
                                      {getShortAddress(task.delivery_address)}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                <div className="min-w-[260px] space-y-2 text-xs text-gray-700">
                                  <div className="flex items-center gap-2">
                                    <CalendarDays
                                      size={13}
                                      className="text-gray-400 shrink-0"
                                    />
                                    <span>
                                      <span className="font-medium text-gray-600">
                                        Delivery:
                                      </span>{" "}
                                      {formatDateTime(task.delivery_date)}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <Clock
                                      size={13}
                                      className="text-gray-400 shrink-0"
                                    />
                                    <span>
                                      <span className="font-medium text-gray-600">
                                        Assigned limit:
                                      </span>{" "}
                                      {formatDateTime(task.assigned_limit_time)}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <Route
                                      size={13}
                                      className="text-gray-400 shrink-0"
                                    />
                                    <span>
                                      {task.taskDistance || "N/A"}
                                      {task.taskEstimatedTime
                                        ? ` • ETA ${task.taskEstimatedTime}`
                                        : ""}
                                    </span>
                                  </div>

                                  <div className="text-gray-500 leading-5">
                                    <span className="font-medium text-gray-600">
                                      Pickup:
                                    </span>{" "}
                                    {getShortAddress(task.pickup_address, 42)}
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                <div className="min-w-[220px] space-y-2 text-xs text-gray-700">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <InfoBadge color="orange">
                                      Driver: {task.driver_name || "Unassigned"}
                                    </InfoBadge>

                                    {task.driver_mobile && (
                                      <InfoBadge color="gray">
                                        {task.driver_mobile}
                                      </InfoBadge>
                                    )}

                                    {task.team_id && (
                                      <InfoBadge color="blue">
                                        Team #{task.team_id}
                                      </InfoBadge>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <Package
                                      size={13}
                                      className="text-gray-400 shrink-0"
                                    />
                                    <span>
                                      {itemCount} item
                                      {itemCount !== 1 ? "s" : ""}
                                    </span>
                                  </div>

                                  <div className="text-gray-500">
                                    Merchant status:{" "}
                                    <span className="font-medium text-gray-700">
                                      {task.merchant_order_status || "N/A"}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                <div className="flex min-w-[170px] flex-col gap-2">
                                  <div>{renderStatusBadge(task.status)}</div>
                                  <div>
                                    {renderStatusBadge(
                                      task.merchant_order_status,
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-1.5">
                                  <button
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-blue-600 transition hover:bg-blue-50"
                                    title="Show Details"
                                    onClick={() => {
                                      setSelectedTask(task);
                                      setAssignmentLogs([]);
                                      setStatusChangeLogs([]);
                                      setShowLogs(false);
                                    }}
                                  >
                                    <Eye size={16} />
                                  </button>

                                  <button
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-green-600 transition hover:bg-green-50"
                                    onClick={() => {}}
                                    title="Docs / Notes"
                                  >
                                    <FileText size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="flex min-h-[220px] items-center justify-center text-sm font-medium text-gray-500">
                      No tasks available for the selected filters.
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 mt-4">
                <div className="text-sm text-gray-600">
                  Page {pageInfo.pageNumber} • Total {pageInfo.totalCount}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePreviousPage}
                    disabled={!pageInfo.previousToken || loading}
                    className="px-4 py-2 rounded-lg border text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  <button
                    type="button"
                    onClick={handleNextPage}
                    disabled={!pageInfo.nextToken || loading}
                    className="px-4 py-2 rounded-lg bg-black text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>

              <TablePagination
                currentPage={currentPage}
                totalCount={filtered.length}
                pageSize={perPage}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </>
      )}

      {activeTab === "search" && (
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-full max-w-[420px]">
                <Search
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-400"
                />

                <input
                  type="text"
                  value={taskSearchWord}
                  onChange={(e) => {
                    setTaskSearchWord(e.target.value);

                    if (!e.target.value.trim()) {
                      setHasTaskSearched(false);
                      setTaskSearchResults([]);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleTaskDetailsSearch();
                    }
                  }}
                  placeholder="Enter task id e.g. 16418"
                  className="h-9 w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 text-xs font-medium text-gray-800 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-100"
                />
              </div>

              <button
                type="button"
                onClick={handleTaskDetailsSearch}
                disabled={!taskSearchWord.trim() || taskDetailsLoading}
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-orange-400 px-4 text-xs font-bold text-white shadow-sm transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {taskDetailsLoading ? (
                  <>
                    <Loader size={14} className="animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search size={14} />
                    Search
                  </>
                )}
              </button>
            </div>
          </div>

          {taskDetailsLoading ? (
            <LoadingScreen />
          ) : hasTaskSearched && taskSearchResults.length > 0 ? (
            <div className="space-y-5">
              {taskSearchResults.map((item, index) => {
                const taskId =
                  item?.pk_task_id ||
                  item?.taskId ||
                  item?.task_id ||
                  item?.id ||
                  taskSearchWord ||
                  "N/A";

                const orderId = item?.order_id || item?.orderid || "N/A";

                const title =
                  item?.task_description ||
                  item?.description ||
                  `Task #${taskId}`;

                const customerName =
                  item?.customer_name ||
                  item?.customerName ||
                  item?.delivery_details?.customer_name ||
                  "N/A";

                const phone =
                  item?.contact_number ||
                  item?.customer_mobile ||
                  item?.phone ||
                  item?.mobile ||
                  item?.delivery_details?.customer_mobile ||
                  "N/A";

                const status = item?.status || item?.task_status || "N/A";
                const merchantStatus = item?.merchant_order_status || "N/A";

                const driverName =
                  item?.driver_name ||
                  item?.driverName ||
                  item?.delivery_person_name ||
                  "N/A";

                const driverMobile = item?.driver_mobile || "N/A";

                const pickupLat = item?.pickup_latitude;
                const pickupLng = item?.pickup_longitude;
                const dropLat = item?.dropoff_latitude || item?.task_lat;
                const dropLng = item?.dropoff_longitude || item?.task_lng;

                const hasMapCoords =
                  (pickupLat && pickupLng) || (dropLat && dropLng);

                return (
                  <div
                    key={item?.pk_task_id || item?.taskId || item?.id || index}
                    className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                  >
                    {/* Header */}
                    <div className="border-b border-gray-100 bg-orange-50/70 px-4 py-3">
                      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-orange-700">
                              <Hash size={12} />
                              Task #{taskId}
                            </span>

                            <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700">
                              <ShoppingBag size={12} />
                              Order #{orderId}
                            </span>

                            {Number(item?.critical) === 1 && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-[11px] font-semibold text-red-700">
                                <ShieldAlert size={12} />
                                Critical
                              </span>
                            )}
                          </div>

                          <h2 className="text-base font-bold text-gray-900 leading-snug">
                            {title}
                          </h2>

                          <p className="mt-0.5 text-[11px] text-gray-500">
                            Created: {formatDisplayDateTime(item?.date_created)}
                          </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <StatusPill status={status} />
                          <StatusPill status={merchantStatus} />

                          <select
                            value={
                              selectedSearchStatus || status?.toLowerCase()
                            }
                            onChange={(e) =>
                              setSelectedSearchStatus(e.target.value)
                            }
                            className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs font-semibold text-gray-700 outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                          >
                            {statusOptions.map((st) => (
                              <option key={st} value={st}>
                                {st.toUpperCase()}
                              </option>
                            ))}
                          </select>

                          <button
                            type="button"
                            onClick={() => handleSearchStatusChange(item)}
                            disabled={
                              searchStatusUpdating || !selectedSearchStatus
                            }
                            className="inline-flex h-8 items-center justify-center rounded-lg bg-orange-400 px-3 text-xs font-bold text-white transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {searchStatusUpdating
                              ? "Updating..."
                              : selectedSearchStatus === "unassigned" &&
                                  item?.status?.toLowerCase() !== "unassigned"
                                ? "Assign Driver"
                                : "Change Status"}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-5 p-5">
                      {/* Map + Main Info */}
                      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.15fr_0.85fr]">
                        {/* Map Left Side */}
                        {hasMapCoords ? (
                          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                            <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                              <div className="flex items-center gap-2">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50 text-orange-500">
                                  <MapPinned size={15} />
                                </div>

                                <div>
                                  <h3 className="text-xs font-bold text-gray-900">
                                    Route Map
                                  </h3>
                                  <p className="text-[11px] text-gray-500">
                                    Pickup and delivery location
                                  </p>
                                </div>
                              </div>

                              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-600">
                                {item?.taskDistance || "N/A"} •{" "}
                                {item?.taskEstimatedTime || "N/A"}
                              </span>
                            </div>

                            <div className="h-[360px]">
                              <MapContainer
                                center={[
                                  Number(dropLat || pickupLat),
                                  Number(dropLng || pickupLng),
                                ]}
                                zoom={14}
                                scrollWheelZoom={false}
                                style={{ height: "100%", width: "100%" }}
                              >
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                                {pickupLat && pickupLng && (
                                  <Marker
                                    position={[
                                      Number(pickupLat),
                                      Number(pickupLng),
                                    ]}
                                  >
                                    <Popup>Pickup Location</Popup>
                                  </Marker>
                                )}

                                {dropLat && dropLng && (
                                  <Marker
                                    position={[
                                      Number(dropLat),
                                      Number(dropLng),
                                    ]}
                                  >
                                    <Popup>Delivery Location</Popup>
                                  </Marker>
                                )}
                              </MapContainer>
                            </div>
                          </div>
                        ) : (
                          <div className="flex min-h-[360px] items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500">
                            Map coordinates not available
                          </div>
                        )}

                        {/* Right Side: Summary + Addresses */}
                        <div className="space-y-3">
                          <div className="rounded-xl border border-gray-200 bg-white p-3">
                            <div className="mb-2 flex items-center gap-2">
                              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50 text-orange-500">
                                <ClipboardList size={15} />
                              </div>

                              <div>
                                <h3 className="text-xs font-bold text-gray-900">
                                  Task Summary
                                </h3>
                                <p className="text-[11px] text-gray-500">
                                  Customer and driver details
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                              <MiniInfo
                                icon={User}
                                label="Customer"
                                value={customerName}
                              />
                              <MiniInfo
                                icon={Phone}
                                label="Contact"
                                value={phone}
                              />
                              <MiniInfo
                                icon={Bike}
                                label="Driver"
                                value={driverName}
                              />
                              <MiniInfo
                                icon={Phone}
                                label="Driver Mobile"
                                value={driverMobile}
                              />
                              <MiniInfo
                                icon={Route}
                                label="Distance"
                                value={item?.taskDistance}
                              />
                              <MiniInfo
                                icon={Clock}
                                label="ETA"
                                value={item?.taskEstimatedTime}
                              />
                              <MiniInfo
                                icon={CalendarDays}
                                label="Delivery Date"
                                value={formatDisplayDateTime(
                                  item?.delivery_date,
                                )}
                              />
                              <MiniInfo
                                icon={CalendarClock}
                                label="Assigned Limit"
                                value={formatDisplayDateTime(
                                  item?.assigned_limit_time,
                                )}
                              />
                            </div>
                          </div>

                          <div className="rounded-xl border border-orange-100 bg-orange-50/40 p-3">
                            <div className="mb-1.5 flex items-center gap-2">
                              <Store size={15} className="text-orange-500" />
                              <h3 className="text-xs font-bold text-gray-900">
                                Pickup Address
                              </h3>
                            </div>

                            <p className="line-clamp-3 text-xs font-semibold leading-5 text-gray-800">
                              {item?.pickup_address || "N/A"}
                            </p>
                          </div>

                          <div className="rounded-xl border border-green-100 bg-green-50/40 p-3">
                            <div className="mb-1.5 flex items-center gap-2">
                              <MapPinned size={15} className="text-green-600" />
                              <h3 className="text-xs font-bold text-gray-900">
                                Delivery Address
                              </h3>
                            </div>

                            <p className="line-clamp-3 text-xs font-semibold leading-5 text-gray-800">
                              {item?.delivery_address || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-gray-200 bg-white">
                        <div className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left bg-white">
                          <div className="flex items-center gap-2">
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-500">
                              <Activity size={17} />
                            </div>

                            <div>
                              <h3 className="text-sm font-bold text-gray-900">
                                Status & Assignment Logs
                              </h3>
                              <p className="text-xs text-gray-500">
                                Logs are automatically shown for this task
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="border-t border-gray-100 p-4">
                          {searchLogsLoading ? (
                            <div className="py-6 text-center text-sm font-medium text-gray-500">
                              Loading logs...
                            </div>
                          ) : searchStatusChangeLogs.length === 0 &&
                            searchAssignmentLogs.length === 0 ? (
                            <div className="py-6 text-center text-sm font-medium text-gray-500">
                              No logs available
                            </div>
                          ) : (
                            <div className="space-y-5">
                              {/* STATUS LOGS */}
                              {searchStatusChangeLogs.length > 0 && (
                                <div className="rounded-xl border border-gray-200 p-4">
                                  <h4 className="mb-5 text-xs font-bold uppercase tracking-wide text-gray-500">
                                    Status Change Logs
                                  </h4>

                                  <div className="overflow-x-auto">
                                    <div className="flex min-w-max items-start justify-center px-2">
                                      {searchStatusChangeLogs.map(
                                        (log, idx) => {
                                          const status =
                                            log?.status?.toLowerCase();

                                          const statusColorMap = {
                                            unassigned: "bg-gray-400",
                                            assigned: "bg-yellow-500",
                                            acknowledged: "bg-blue-500",
                                            arrived: "bg-green-500",
                                            "food is ready": "bg-purple-500",
                                            "picked up": "bg-orange-500",
                                            successful: "bg-green-700",
                                            completed: "bg-green-700",
                                            cancelled: "bg-red-500",
                                            failed: "bg-red-500",
                                            declined: "bg-pink-500",
                                          };

                                          const lineColorMap = {
                                            unassigned: "bg-gray-200",
                                            assigned: "bg-yellow-200",
                                            acknowledged: "bg-blue-200",
                                            arrived: "bg-green-200",
                                            "food is ready": "bg-purple-200",
                                            "picked up": "bg-orange-200",
                                            successful: "bg-green-200",
                                            completed: "bg-green-200",
                                            cancelled: "bg-red-200",
                                            failed: "bg-red-200",
                                            declined: "bg-pink-200",
                                          };

                                          const bgColor =
                                            statusColorMap[status] ||
                                            "bg-blue-500";
                                          const lineColor =
                                            lineColorMap[status] ||
                                            "bg-blue-200";

                                          return (
                                            <React.Fragment key={idx}>
                                              <div className="flex min-w-[105px] flex-col items-center">
                                                <div
                                                  className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white shadow-sm ring-4 ring-white ${bgColor}`}
                                                >
                                                  {idx + 1}
                                                </div>

                                                <div className="mt-2 max-w-[95px] text-center text-[10px] font-bold uppercase leading-4 text-gray-700">
                                                  {log?.status || "N/A"}
                                                </div>
                                              </div>

                                              {idx !==
                                                searchStatusChangeLogs.length -
                                                  1 && (
                                                <div
                                                  className={`mt-5 h-[2px] w-12 ${lineColor}`}
                                                />
                                              )}
                                            </React.Fragment>
                                          );
                                        },
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* ASSIGNMENT LOGS */}
                              {searchAssignmentLogs.length > 0 && (
                                <div className="rounded-xl border border-gray-200 p-4">
                                  <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">
                                    Assignment Logs
                                  </h4>

                                  <div className="overflow-x-auto rounded-xl border border-gray-100">
                                    <table className="min-w-full text-xs">
                                      <thead className="bg-orange-50 text-orange-700">
                                        <tr>
                                          <th className="px-3 py-2 text-left font-bold">
                                            Assign Type
                                          </th>
                                          <th className="px-3 py-2 text-left font-bold">
                                            Driver ID
                                          </th>
                                          <th className="px-3 py-2 text-left font-bold">
                                            Driver Name
                                          </th>
                                          <th className="px-3 py-2 text-left font-bold">
                                            Task Status
                                          </th>
                                          <th className="px-3 py-2 text-left font-bold">
                                            Status
                                          </th>
                                          <th className="px-3 py-2 text-left font-bold">
                                            Assignment Date
                                          </th>
                                          <th className="px-3 py-2 text-left font-bold">
                                            Process Date
                                          </th>
                                        </tr>
                                      </thead>

                                      <tbody className="divide-y divide-gray-100 bg-white">
                                        {searchAssignmentLogs.map(
                                          (log, idx) => (
                                            <tr
                                              key={idx}
                                              className="hover:bg-orange-50/40"
                                            >
                                              <td className="px-3 py-2 font-medium text-gray-800">
                                                {log?.assignType ||
                                                  log?.assign_type ||
                                                  "N/A"}
                                              </td>
                                              <td className="px-3 py-2">
                                                {log?.driverId ||
                                                  log?.driver_id ||
                                                  "N/A"}
                                              </td>
                                              <td className="px-3 py-2 font-semibold text-gray-900">
                                                {log?.driverName?.trim?.() ||
                                                  log?.driver_name ||
                                                  "N/A"}
                                              </td>
                                              <td className="px-3 py-2">
                                                {log?.taskStatus ||
                                                  log?.task_status ||
                                                  "N/A"}
                                              </td>
                                              <td className="px-3 py-2">
                                                <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-700">
                                                  {log?.status || "N/A"}
                                                </span>
                                              </td>
                                              <td className="px-3 py-2">
                                                {log?.assignmentDate ||
                                                  log?.assignment_date ||
                                                  "N/A"}
                                              </td>
                                              <td className="px-3 py-2">
                                                {log?.processDate ||
                                                  log?.process_date ||
                                                  "N/A"}
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Order Details */}
                      <div className="rounded-2xl border border-gray-200 bg-white p-4">
                        <div className="mb-4 flex items-center gap-2">
                          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-orange-500">
                            <ShoppingBag size={17} />
                          </div>
                          <div>
                            <h3 className="text-sm font-bold text-gray-900">
                              Order Details
                            </h3>
                            <p className="text-xs text-gray-500">
                              Merchant, order, assignment and verification
                              information
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <MiniInfo
                            icon={Hash}
                            label="Order ID"
                            value={item?.order_id}
                          />
                          <MiniInfo
                            icon={Store}
                            label="Merchant Order ID"
                            value={item?.merchant_order_id}
                          />
                          <MiniInfo
                            icon={BadgeCheck}
                            label="Merchant Status"
                            value={item?.merchant_order_status}
                          />
                          <MiniInfo
                            icon={Truck}
                            label="Transaction Type"
                            value={item?.trans_type}
                          />
                          <MiniInfo
                            icon={User}
                            label="User Type"
                            value={item?.user_type}
                          />
                          <MiniInfo
                            icon={Hash}
                            label="User ID"
                            value={item?.user_id}
                          />
                          <MiniInfo
                            icon={User}
                            label="Team ID"
                            value={item?.team_id}
                          />
                          <MiniInfo
                            icon={Bike}
                            label="Driver ID"
                            value={item?.driver_id}
                          />
                          <MiniInfo
                            icon={Clock}
                            label="Assign Started"
                            value={formatDisplayDateTime(item?.assign_started)}
                          />
                          <MiniInfo
                            icon={Clock}
                            label="Modified"
                            value={formatDisplayDateTime(item?.date_modified)}
                          />
                          <MiniInfo
                            icon={BadgeCheck}
                            label="Delivery Verified"
                            value={item?.delivery_verified}
                          />
                          <MiniInfo
                            icon={Hash}
                            label="Verification Code"
                            value={item?.delivery_verification_code}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : hasTaskSearched ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-orange-200 bg-orange-50/40 text-center">
              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-orange-100 text-orange-500">
                <Search size={20} />
              </div>

              <h3 className="text-sm font-semibold text-gray-900">
                No data found
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                No task details found for{" "}
                <span className="font-semibold text-orange-600">
                  {taskSearchWord}
                </span>
              </p>
            </div>
          ) : (
            <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white text-center shadow-sm">
              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-orange-100 text-orange-500">
                <Search size={20} />
              </div>

              <h3 className="text-sm font-semibold text-gray-900">
                Search task details
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Enter task id and click search.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <TaskDetailsModal
        show={!!selectedTask}
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
        assignmentLogs={assignmentLogs}
        statusChangeLogs={statusChangeLogs}
        showLogs={showLogs}
        setShowLogs={setShowLogs}
        onFetchLogs={async (taskId) => {
          const data = await fetchTaskLogs(taskId, fromDate, toDate);

          setAssignmentLogs(data?.assignmentLogs || []);
          setStatusChangeLogs(data?.statusChangeLogs || []);
        }}
        onStatusChange={async (taskId, newStatus) => {
          await changeTaskStatus(selectedTask, newStatus, () =>
            fetchTasks(fromDate, toDate, setTasks),
          );

          setSelectedTask(null);
        }}
      />

      <AssignModal
        show={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        teams={teams}
        assignTab={assignTab}
        setAssignTab={setAssignTab}
        selectedAssignTeam={selectedAssignTeam}
        setSelectedAssignTeam={setSelectedAssignTeam}
        teamDrivers={teamDrivers}
        allTeamDrivers={allTeamDrivers}
        selectedAgentId={selectedAgentId}
        setSelectedAgentId={setSelectedAgentId}
        handleAssignTask={async () => {
          if (!selectedAgentId || !taskToAssign) return;

          try {
            await changeTaskStatus(taskToAssign, "unassigned");

            const success = await AssignTaskToDriver(
              taskToAssign.pk_task_id,
              selectedAgentId,
            );

            if (success) {
              fetchTasks(fromDate, toDate, setTasks, {
                pageSize: pageInfo.pageSize,
                nextToken: "",
                previousToken: "",
                setPageInfo,
              });

              // Refresh Search By Id result also
              const taskId =
                taskToAssign?.pk_task_id ||
                taskToAssign?.taskId ||
                taskToAssign?.task_id ||
                taskToAssign?.id;

              if (activeTab === "search" && taskId) {
                const updatedTask = await fetchTaskDetails(taskId);

                const safeData = Array.isArray(updatedTask)
                  ? updatedTask
                  : updatedTask
                    ? [updatedTask]
                    : [];

                setTaskSearchResults(safeData);

                if (safeData?.[0]?.status) {
                  setSelectedSearchStatus(safeData[0].status.toLowerCase());
                }

                setSearchShowLogs(true);
                setSearchAssignmentLogs([]);
                setSearchStatusChangeLogs([]);
              }

              setShowAssignModal(false);
              setSelectedAgentId("");
              setTaskToAssign(null);
            }
          } catch (err) {
            console.error("Assign Flow Error:", err);
          }
        }}
      />
    </div>
  );
}
